import { downloadFile, requestJson, requestMultipart, viewFile } from "@/lib/api";

const BASE = "api/v1/admin/supplier-invoices";

/**
 * Sugestão (padrão memorizado do fornecedor) para um item ainda pendente. `applicable` só é true quando o destino
 * continua ativo/elegível, há fator memorizado e a UNIDADE COMERCIAL casa com a do item — nesse caso a sugestão
 * entra na confirmação em lote. Caso contrário, `reviewReason` explica a divergência (unidade comercial diferente,
 * destino inativo ou sem fator). Preenche o editor para CONFERÊNCIA; a confirmação é sempre EXPLÍCITA.
 */
export interface SupplierItemMappingSuggestion {
  destinationType: "MATERIA_PRIMA_IN_NATURA" | "MATERIAL_EMBALAGEM" | string;
  productId: string | null;
  productCode: number | null;
  productName: string | null;
  materialId: string | null;
  materialName: string | null;
  purchaseUnit: string | null;
  conversionFactor: string | null;
  commercialUnit: string | null;
  unitMatches: boolean;
  destinationActive: boolean;
  applicable: boolean;
  reviewReason: string | null;
}

export interface SupplierInvoiceItem {
  id: string;
  nItem: number;
  supplierCode: string | null;
  description: string | null;
  ncm: string | null;
  cfop: string | null;
  uCom: string | null;
  qCom: string | null;
  vUnCom: string | null;
  vProd: string | null;
  destinationType: string;
  mappedProductId: string | null;
  mappedMaterialId: string | null;
  mappedProductCode: number | null;
  mappedProductName: string | null;
  // unidade de referência do produto mapeado (ex.: "Kg"); mantém a unidade no rótulo/prévia após salvar/reabrir
  mappedProductUnit: string | null;
  mappedMaterialName: string | null;
  purchaseUnit: string | null;
  conversionFactor: string | null;
  mappingComplete: boolean;
  // Sugestão do padrão do fornecedor (só presente em item pendente com padrão memorizado); null caso contrário.
  suggestion: SupplierItemMappingSuggestion | null;
}

/**
 * Identificação do emitente: o que o XML declara (razão social, fantasia, IE, município/UF — nulos em NFs
 * antigas até o backfill) e o fornecedor CADASTRADO que casa pelo documento (null = não cadastrado).
 */
export interface SupplierInvoiceEmitter {
  name: string | null;
  tradeName: string | null;
  ie: string | null;
  city: string | null;
  uf: string | null;
  supplierId: string | null;
  supplierName: string | null;
}

export interface SupplierInvoice {
  id: string;
  accessKey: string;
  xmlSha256: string;
  emitterCnpj: string | null;
  // CPF do emitente (produtor rural) e o documento resolvido (CNPJ ou CPF) para exibição/identificação
  emitterCpf: string | null;
  emitterDocument: string | null;
  emitter: SupplierInvoiceEmitter;
  destCnpj: string | null;
  number: string | null;
  series: string | null;
  emittedAt: string | null;
  // eixos fiscais SEPARADOS
  integrityOk: boolean;
  signatureOk: boolean;
  chainTrusted: boolean;
  revocation: string;
  fiscalSituation: string;
  statusLabel: string;
  trustedForConfirm: boolean;
  importStatus: "PREVIA" | "CONFIRMADA" | "DESCARTADA" | "INVALIDADA";
  totalNfe: string | null;
  totalFreight: string | null;
  totalDiscount: string | null;
  confirmedAt: string | null;
  // origem e autoria (fim do "dono" da prévia): quem subiu ("SEFAZ" na integração DF-e) e quem aceitou
  origin: "UPLOAD" | "SEFAZ_DFE";
  importedByName: string | null;
  confirmedByName: string | null;
  // invalidação (Entrega 2B): preenchidos quando importStatus === "INVALIDADA"
  invalidatedAt: string | null;
  invalidationReason: string | null;
  items: SupplierInvoiceItem[];
}

export interface SupplierInvoiceSummary {
  id: string;
  accessKey: string;
  number: string | null;
  series: string | null;
  emitterCnpj: string | null;
  emitterCpf: string | null;
  emitterDocument: string | null;
  emitter: SupplierInvoiceEmitter;
  destCnpj: string | null;
  emittedAt: string | null;
  // data de IMPORTAÇÃO (createdAt) — é a data que o filtro de período usa
  importedAt: string;
  importStatus: "PREVIA" | "CONFIRMADA" | "DESCARTADA" | "INVALIDADA";
  statusLabel: string;
  trustedForConfirm: boolean;
  totalNfe: string | null;
  confirmedAt: string | null;
  itemCount: number;
  // Mapeamento pendente (indicador DERIVADO; a NF segue CONFIRMADA): true quando há itens sem destino/fator.
  // pendingItemCount diz quantos faltam. 0/false quando não confirmada ou tudo mapeado.
  mappingPending: boolean;
  pendingItemCount: number;
  // "subida por mim" (não há mais dono da prévia; o backend decide quem pode agir)
  ownedByMe: boolean;
  origin: "UPLOAD" | "SEFAZ_DFE";
  importedByName: string | null;
  confirmedByName: string | null;
}

export interface SupplierInvoicePage {
  content: SupplierInvoiceSummary[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/** Filtros da consulta de NFs. `status` vazio = padrão (exclui DESCARTADA). `from`/`to` = período por
 *  DATA DE IMPORTAÇÃO; `emittedFrom`/`emittedTo` = período por DATA DE EMISSÃO (yyyy-mm-dd), SEPARADO da
 *  importação. A busca livre `q` já cobre nome do emitente + documento + número + chave. `number` = número
 *  da NF em campo DEDICADO (correspondência EXATA, separado de `q`). `minValue`/`maxValue` = faixa INCLUSIVA
 *  sobre o total da NF (texto decimal; a vírgula é normalizada para ponto antes do envio). `mine` restringe
 *  às próprias. Toda a consulta é LOCAL — não dispara SEFAZ. */
export interface SupplierInvoiceListParams {
  q?: string;
  status?: "" | "PREVIA" | "CONFIRMADA" | "DESCARTADA" | "INVALIDADA";
  from?: string;
  to?: string;
  emittedFrom?: string;
  emittedTo?: string;
  number?: string;
  minValue?: string;
  maxValue?: string;
  /** Filtro FISCAL de manifestação (SEFAZ), SEPARADO da Validação Naturexpress: ''=todas, 'KNOWN', 'NONE'. */
  manifestation?: "" | "KNOWN" | "NONE";
  mine?: boolean;
  page?: number;
  size?: number;
}

/** Normaliza um limite de valor digitado (pt-BR) para decimal com ponto; vazio vira undefined (sem filtro). */
const normalizeMoney = (v: string | undefined): string | undefined => {
  const t = (v ?? "").trim();
  if (!t) return undefined;
  // Com vírgula = pt-BR (ponto é milhar, vírgula é decimal); sem vírgula o ponto já é o decimal.
  return t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t;
};

/** Lista resumida (paginada) das NFs, com busca (nome/chave/número/CPF/CNPJ) e filtros. Consulta LOCAL. */
export async function listSupplierInvoices(params: SupplierInvoiceListParams = {}): Promise<SupplierInvoicePage> {
  const sp = new URLSearchParams();
  sp.set("q", params.q ?? "");
  if (params.status) sp.set("status", params.status);
  if (params.from) sp.set("from", params.from);
  if (params.to) sp.set("to", params.to);
  if (params.emittedFrom) sp.set("emittedFrom", params.emittedFrom);
  if (params.emittedTo) sp.set("emittedTo", params.emittedTo);
  if (params.number && params.number.trim()) sp.set("number", params.number.trim());
  const min = normalizeMoney(params.minValue);
  const max = normalizeMoney(params.maxValue);
  if (min) sp.set("minValue", min);
  if (max) sp.set("maxValue", max);
  if (params.manifestation) sp.set("manifestation", params.manifestation);
  if (params.mine) sp.set("mine", "true");
  sp.set("page", String(params.page ?? 0));
  sp.set("size", String(params.size ?? 20));
  return requestJson<SupplierInvoicePage>(`${BASE}?${sp.toString()}`);
}

/**
 * Resultado da busca manual DF-e (SEFAZ). `outcome` guia a UI (OK/NO_NEW_DOCUMENTS/IDEMPOTENT_HIT/REPROCESSED =
 * sucesso; THROTTLED/LOCKED/BLOCKED/NOT_READY/DISABLED = não executou; FAILED_BEFORE_SEND/SENT_UNKNOWN/FAILED_LOCAL
 * = estado registrado). `nextAllowedAt` indica a partir de quando uma nova consulta externa é permitida.
 */
export interface DfeSearchResult {
  outcome: string;
  status: string | null;
  docs: number;
  resumos: number;
  previas: number;
  rejected: number;
  failed: number;
  cursorAdvanced: boolean;
  ultNsu: string | null;
  nextAllowedAt: string | null;
  message: string | null;
}

/**
 * Busca MANUAL de NF-e na SEFAZ para um estabelecimento — UM lote por clique. `requestId` é a chave de
 * idempotência: reenviar o MESMO id nunca dispara nova chamada fiscal (devolve o estado conhecido ou reprocessa
 * localmente). `confirmRetryAfterUncertain` autoriza explicitamente nova tentativa após um resultado incerto.
 */
/**
 * Estado da sincronização DF-e (cursor NSU, última execução, certificado) — SEM segredos. `ultNsu` = última
 * posição lida; `maxNsu` = máxima informada pela SEFAZ. A diferença são POSIÇÕES de NSU (cada posição pode
 * ser nota, resumo ou evento), NÃO quantidade de notas.
 */
export interface DfeStatus {
  enabled: boolean;
  ready: boolean;
  tpAmb: number;
  cnpj: string;
  ultNsu: string | null;
  maxNsu: string | null;
  lastRunAt: string | null;
  lastStatus: string | null;
  lastMessage: string | null;
  certificate: string;
  cienciasPendentes: number;
  aguardandoXml: number;
  automationContained: boolean;
  cycleBlocked: boolean;
  manualSearchEnabled: boolean;
  manualSearchBlocked: boolean;
  manualSearchBlockReason: string | null;
  certSource: string;
}

/** Lê o estado da sincronização DF-e (não dispara consulta fiscal). Exige SINCRONIZAR — uso do Buscador (ADMIN). */
export async function getDfeStatus(): Promise<DfeStatus> {
  return requestJson<DfeStatus>(`${BASE}/dfe-status`);
}

/**
 * Estado RESUMIDO da base para a PESQUISA local (tela Notas de entrada): `lastUpdateAt` = última persistência
 * BEM-SUCEDIDA de documentos (erro/656 não avança); `pendingPositions` = posições de NSU ainda não lidas.
 * Exige só VISUALIZAR — não depende de SINCRONIZAR nem do cargo ADMIN.
 */
export interface DfeBaseStatus {
  lastUpdateAt: string | null;
  pendingPositions: number | null;
}

export async function getDfeBaseStatus(): Promise<DfeBaseStatus> {
  return requestJson<DfeBaseStatus>(`${BASE}/dfe-base-status`);
}

export async function dfeSearch(req: {
  establishmentId: string;
  requestId: string;
  confirmRetryAfterUncertain?: boolean;
}): Promise<DfeSearchResult> {
  return requestJson<DfeSearchResult>(`${BASE}/dfe-search`, {
    method: "POST",
    body: {
      establishmentId: req.establishmentId,
      requestId: req.requestId,
      confirmRetryAfterUncertain: req.confirmRetryAfterUncertain ?? false,
    },
  });
}

/** Importa o XML da NF-e → prévia persistida. `replace` substitui explicitamente uma prévia diagnóstica. */
export async function previewSupplierInvoice(file: File, replace = false): Promise<SupplierInvoice> {
  const form = new FormData();
  form.append("file", file);
  return requestMultipart<SupplierInvoice>(`${BASE}/preview?replace=${replace ? "true" : "false"}`, form);
}

/** Confirma a prévia conferida enviando APENAS a referência de revisão (hash esperado), nunca o XML. */
export async function confirmSupplierInvoice(accessKey: string, expectedSha256: string): Promise<SupplierInvoice> {
  return requestJson<SupplierInvoice>(`${BASE}/${encodeURIComponent(accessKey)}/confirm`, {
    method: "POST",
    body: { expectedSha256 },
  });
}

export async function getSupplierInvoice(accessKey: string): Promise<SupplierInvoice> {
  return requestJson<SupplierInvoice>(`${BASE}/${encodeURIComponent(accessKey)}`);
}

/**
 * Descarte (soft) de uma PRÉVIA própria (IMPORTAR + proprietário). Só PREVIA pode ser descartada
 * (confirmada → 409). Preserva XML/auditoria; a prévia some da lista padrão (consultável por filtro).
 */
export async function discardSupplierInvoice(accessKey: string): Promise<SupplierInvoice> {
  return requestJson<SupplierInvoice>(`${BASE}/${encodeURIComponent(accessKey)}/discard`, {
    method: "POST",
  });
}

/**
 * Invalida uma importação CONFIRMADA (permissão INVALIDAR; motivo obrigatório). Estado terminal, sem
 * reversão: preserva XML, vínculos e saldos consumidos; bloqueia novos mapeamentos/vínculos. Invalidar no
 * sistema NÃO cancela a NF na SEFAZ.
 */
export async function invalidateSupplierInvoice(accessKey: string, reason: string): Promise<SupplierInvoice> {
  return requestJson<SupplierInvoice>(`${BASE}/${encodeURIComponent(accessKey)}/invalidate`, {
    method: "POST",
    body: { reason },
  });
}

/**
 * Revalida a prévia persistida: recomputa os eixos fiscais contra o truststore ATUAL, SEM confirmar.
 * Necessária porque reimportar o mesmo XML é idempotente e não recalcula o diagnóstico salvo.
 */
export async function revalidateSupplierInvoice(accessKey: string): Promise<SupplierInvoice> {
  return requestJson<SupplierInvoice>(`${BASE}/${encodeURIComponent(accessKey)}/revalidate`, {
    method: "POST",
  });
}

/**
 * Perfil cadastral do CNPJ emitente (Receita Federal via BrasilAPI, com cache no backend). `available=false`
 * quando não há dados (CPF, consulta desligada ou falha sem cache); `stale=true` quando o snapshot é antigo e a
 * renovação falhou (`error` explica).
 */
export interface EmitterProfile {
  available: boolean;
  cnpj: string | null;
  razaoSocial: string | null;
  nomeFantasia: string | null;
  situacaoCadastral: string | null;
  dataSituacaoCadastral: string | null;
  dataInicioAtividade: string | null;
  naturezaJuridica: string | null;
  porte: string | null;
  cnaeFiscal: string | null;
  cnaeFiscalDescricao: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  municipio: string | null;
  uf: string | null;
  cep: string | null;
  telefone: string | null;
  email: string | null;
  capitalSocial: string | null;
  fetchedAt: string | null;
  // última TENTATIVA (igual a fetchedAt quando deu certo; instante da falha quando não)
  attemptedAt: string | null;
  stale: boolean;
  error: string | null;
}

export async function getEmitterProfile(accessKey: string, refresh = false): Promise<EmitterProfile> {
  return requestJson<EmitterProfile>(
    `${BASE}/${encodeURIComponent(accessKey)}/emitter-profile${refresh ? "?refresh=true" : ""}`,
  );
}

/** Baixa a representação em PDF da NF-e (espelho do XML armazenado). Resumos sem XML → 422 (sem PDF). */
export async function downloadSupplierInvoicePdf(accessKey: string): Promise<void> {
  await downloadFile(`${BASE}/${encodeURIComponent(accessKey)}/pdf?download=true`, `NFe-${accessKey}.pdf`);
}

/** Abre a representação em PDF da NF-e (espelho do XML) em nova aba. Resumos sem XML → 422 (sem PDF). */
export async function viewSupplierInvoicePdf(accessKey: string): Promise<void> {
  await viewFile(`${BASE}/${encodeURIComponent(accessKey)}/pdf`);
}

// ---------------------------------------------------------------- PDF ORIGINAL do fornecedor (anexo)

export interface SupplierPdfMeta {
  id: string;
  fileName: string | null;
  contentSha256: string;
  sizeBytes: number;
  uploadedBy: string;
  uploadedAt: string;
  active: boolean;
  supersededAt: string | null;
}

export interface SupplierPdfCurrent {
  attached: boolean;
  meta: SupplierPdfMeta | null;
}

/** Metadados do PDF do fornecedor vigente (anexado ou não). */
export async function getSupplierPdfMeta(accessKey: string): Promise<SupplierPdfCurrent> {
  return requestJson<SupplierPdfCurrent>(`${BASE}/${encodeURIComponent(accessKey)}/supplier-pdf/meta`);
}

/** Anexa (ou substitui, com replace=true) o PDF ORIGINAL do fornecedor. Valida PDF real no backend. */
export async function attachSupplierPdf(accessKey: string, file: File, replace: boolean): Promise<SupplierPdfMeta> {
  const form = new FormData();
  form.append("file", file);
  return requestMultipart<SupplierPdfMeta>(
    `${BASE}/${encodeURIComponent(accessKey)}/supplier-pdf?replace=${replace ? "true" : "false"}`, form);
}

/** Abre o PDF do fornecedor anexado em nova aba. 404 se não anexado. */
export async function viewSupplierPdfOriginal(accessKey: string): Promise<void> {
  await viewFile(`${BASE}/${encodeURIComponent(accessKey)}/supplier-pdf`);
}

/** Baixa o PDF do fornecedor anexado. 404 se não anexado. */
export async function downloadSupplierPdfOriginal(accessKey: string): Promise<void> {
  await downloadFile(`${BASE}/${encodeURIComponent(accessKey)}/supplier-pdf?download=true`,
    `NF-fornecedor-${accessKey}.pdf`);
}

export async function downloadSupplierInvoiceXml(accessKey: string): Promise<void> {
  await downloadFile(`${BASE}/${encodeURIComponent(accessKey)}/xml`, `NFe-${accessKey}.xml`);
}

export interface ItemBalance {
  invoiceItemId: string;
  supplierCode: string | null;
  nfQuantity: string;
  linkedQuantity: string;
  remainingQuantity: string;
}

export interface LinkView {
  id: string;
  invoiceItemId: string;
  receivingItemId: string;
  originalQuantity: string;
  originalUnit: string | null;
  convertedQuantity: string;
  destinationUnit: string | null;
  factorUsed: string;
  createdAt: string;
  // Dados descritivos do recebimento vinculado, para consulta na tela (persistem após recarregar).
  receivedAt: string | null;
  supplierName: string | null;
  productName: string | null;
  receivingUnit: string | null;
  receivingQuantity: string | null;
}

/**
 * Mapeia o item ao destino (produto IN NATURA ou material), com unidade de compra e fator de conversão.
 * `updateSupplierDefault` (padrão false): quando o item corrige um padrão já memorizado do fornecedor, true
 * atualiza o padrão das PRÓXIMAS notas; false aplica a correção só a esta nota. No primeiro mapeamento o padrão
 * é sempre salvo automaticamente, independentemente da flag.
 */
export async function mapInvoiceItem(itemId: string, req: {
  destinationType: "MATERIA_PRIMA_IN_NATURA" | "MATERIAL_EMBALAGEM";
  productId?: string | null;
  materialId?: string | null;
  purchaseUnit?: string | null;
  conversionFactor?: number | null;
  updateSupplierDefault?: boolean;
}): Promise<void> {
  await requestJson<void>(`${BASE}/items/${encodeURIComponent(itemId)}/map`, { method: "POST", body: req });
}

/** Resultado da confirmação em lote das sugestões (itens sem divergência aplicados numa única ação). */
export interface ApplySuggestionsResult {
  applied: number;
  skipped: number;
  skippedReasons: string[];
}

/**
 * Confirma EM LOTE as sugestões (padrão memorizado do fornecedor) dos itens pendentes SEM divergência da NF.
 * `itemIds` restringe o lote a itens específicos (os NÃO editados manualmente); omitido = todos os aplicáveis.
 * Itens com divergência/sem padrão são pulados (reportados em `skippedReasons`) para revisão manual.
 */
export async function applyMapSuggestions(accessKey: string, itemIds?: string[]): Promise<ApplySuggestionsResult> {
  return requestJson<ApplySuggestionsResult>(
    `${BASE}/${encodeURIComponent(accessKey)}/map-suggestions`,
    { method: "POST", body: { itemIds: itemIds ?? null } });
}

/** Vincula um item da NF a um item de recebimento existente (quantidade na unidade da NF). */
export async function createLink(req: {
  invoiceItemId: string;
  receivingItemId: string;
  originalQuantity: number;
}): Promise<LinkView> {
  return requestJson<LinkView>(`${BASE}/links`, { method: "POST", body: req });
}

export async function getBalances(accessKey: string): Promise<ItemBalance[]> {
  return requestJson<ItemBalance[]>(`${BASE}/${encodeURIComponent(accessKey)}/balances`);
}

export async function getLinks(accessKey: string): Promise<LinkView[]> {
  return requestJson<LinkView[]>(`${BASE}/${encodeURIComponent(accessKey)}/links`);
}

// ---------------------------------------------------------------- NF → custos

/** Linha da prévia de custo (componentes separados; proposto × vigente). Valores em texto (plainString). */
export interface NfCostPreviewItem {
  invoiceItemId: string;
  nItem: number;
  description: string | null;
  supplierCode: string | null;
  destinationType: string;
  productId: string | null;
  materialId: string | null;
  targetName: string | null;
  // custo de compra simplificado, componentes separados
  productValue: string | null;
  freight: string | null;
  insurance: string | null;
  other: string | null;
  discount: string | null;
  netCost: string | null;
  // conversão
  uCom: string | null;
  qCom: string | null;
  purchaseUnit: string | null;
  conversionFactor: string | null;
  convertedQuantity: string | null;
  referenceUnit: string | null;
  // custo
  proposedCost: string | null;
  currentCost: string | null;
  currentUnit: string | null;
  currentReferenceDate: string | null;
  currentVersion: number | null;
  variationPercent: string | null;
  // status
  eligible: boolean;
  alreadyApplied: boolean;
  blockReason: string | null;
  olderThanCurrent: boolean;
}

export interface NfCostPreview {
  invoiceId: string;
  accessKey: string;
  number: string | null;
  emittedAt: string | null;
  supplierDocument: string | null;
  supplierName: string | null;
  confirmed: boolean;
  chargeDivergence: boolean;
  notes: string[];
  items: NfCostPreviewItem[];
}

export interface NfCostApplyItem {
  invoiceItemId: string;
  expectedProposedCost: string | null;
  expectedTargetVersion: number | null;
  confirmReplaceNewer: boolean;
  // Assinatura vista na prévia — o backend recusa se destino/alvo/fator ou a unidade de referência mudaram.
  expectedDestinationType: string | null;
  expectedProductId: string | null;
  expectedMaterialId: string | null;
  expectedReferenceUnit: string | null;
  expectedConversionFactor: string | null;
}

export interface NfCostApplyRequest {
  invoiceId: string;
  acknowledgeChargeDivergence: boolean;
  items: NfCostApplyItem[];
}

export interface NfCostAppliedItem {
  invoiceItemId: string;
  nItem: number;
  destinationType: string;
  targetName: string | null;
  previousCost: string | null;
  appliedCost: string | null;
  referenceUnit: string | null;
  olderThanCurrent: boolean;
}

export interface NfCostRejectedItem {
  invoiceItemId: string;
  nItem: number;
  reason: string;
}

export interface NfCostApplyResponse {
  applied: NfCostAppliedItem[];
  rejected: NfCostRejectedItem[];
}

/** Prévia do custo de compra por item de uma NF CONFIRMADA (recalculada no backend sobre TODOS os itens). */
export async function getNfCostPreview(invoiceId: string): Promise<NfCostPreview> {
  return requestJson<NfCostPreview>(`${BASE}/${encodeURIComponent(invoiceId)}/cost/preview`);
}

/** Aplica o custo RECALCULADO pelo backend aos itens selecionados (os valores enviados só detectam divergência). */
export async function applyNfCost(req: NfCostApplyRequest): Promise<NfCostApplyResponse> {
  return requestJson<NfCostApplyResponse>(`${BASE}/cost/apply`, { method: "POST", body: req });
}

export interface ProductOption {
  id: string;
  code: number | null;
  name: string;
  unit: string | null;
}

export interface MaterialOption {
  id: string;
  name: string;
  unit: string | null;
}

export interface ReceivingItemOption {
  receivingItemId: string;
  receivingId: string;
  receivedAt: string;
  supplierName: string;
  establishmentName: string;
  productName: string;
  unit: string;
  quantity: string;
  linkedQuantity: string;
  remainingQuantity: string;
}

/** Busca produtos IN NATURA por nome/código para o seletor de mapeamento. */
export async function searchProducts(q: string): Promise<ProductOption[]> {
  return requestJson<ProductOption[]>(`${BASE}/search/products?q=${encodeURIComponent(q)}`);
}

/** Busca materiais de embalagem por nome para o seletor de mapeamento. */
export async function searchMaterials(q: string): Promise<MaterialOption[]> {
  return requestJson<MaterialOption[]>(`${BASE}/search/materials?q=${encodeURIComponent(q)}`);
}

/** Lista itens de recebimento vinculáveis a um item da NF (com saldo restante). */
export async function getLinkableReceivingItems(accessKey: string, itemId: string): Promise<ReceivingItemOption[]> {
  return requestJson<ReceivingItemOption[]>(
    `${BASE}/${encodeURIComponent(accessKey)}/linkable-receivings?itemId=${encodeURIComponent(itemId)}`,
  );
}
